import './libs/sentry/sentryInit';

import * as Sentry from '@sentry/react';
import ReactDOM from 'react-dom/client';

import '@pfida-finance/ui/fonts/figtree';
import '@pfida-finance/ui/fonts/jeko';
import '@pfida-finance/ui/global.css';
import '@repo/ui/globals.css';
import './globals.css';

import { mifeName } from '@repo/single-spa';
import { captureAttribution, clientEnv, isTenant } from '@repo/utils';
import { registerApplication } from 'single-spa';

import { MaintenanceModeWrapper } from './components/maintenanceModeWrapper';
import { authStore, hydrateAuth } from './features/auth/store/authStore';
import { startConsentBridge } from './libs/consentBridge';
import { setGoogleConsentDefaults } from './libs/googleConsent';
import { loadHubSpot } from './libs/hubspotConsent';
import { loadMetaPixel } from './libs/metaPixel';
import { loadTagManager } from './libs/tagManager';
import { Router, router } from './libs/tanstackRouter/router';
import { startTrackingBoundary } from './libs/trackingBoundary';
import { AppProviders } from './providers';

registerApplication({
  name: mifeName,
  app: () => import(/* @vite-ignore */ mifeName).then((data) => data.vueParcel()),
  activeWhen: ['/'],
});

hydrateAuth();

// Pfida only: consent banner first; campaign read before the router drops it (stored only with
// advertising consent); Pixel revoked until the consent bridge grants it from the advertising choice
if (isTenant('pfida')) {
  // Google's consent starts denied; the consent bridge below applies the recorded choice before Tag
  // Manager reads the data layer, so tags that check consent once, when their trigger fires, see the choice
  setGoogleConsentDefaults();
  // every public page sends a signed in customer to the dashboard, so any page counts as an account page for them
  const trackingPath = authStore.getState().isAuthenticated() ? '/dashboard' : window.location.pathname;
  loadHubSpot(trackingPath);
  captureAttribution(window.location.search, window.location.pathname);
  loadMetaPixel({
    pixelId: clientEnv.VITE_META_PIXEL_ID,
    scriptUrl: clientEnv.VITE_META_PIXEL_SCRIPT_URL,
    pathname: trackingPath,
  });
  startConsentBridge();
}

// every brand: Tag Manager on every page, after the consent above; the Pixel and HubSpot's tracker only
// on public pages, and neither follows into a signed in page
loadTagManager();
startTrackingBoundary(router);

function App() {
  return (
    <AppProviders>
      <MaintenanceModeWrapper>
        <Router />
      </MaintenanceModeWrapper>
    </AppProviders>
  );
}

// Render the app
const rootElement = document.getElementById('app')!;
if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement, {
    // Callback called when React automatically recovers from errors.
    onRecoverableError: Sentry.reactErrorHandler(),
  });
  root.render(<App />);
}
